import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { EmailSequenceData } from '@/pages/EmailSequence';
import { Loader2 } from 'lucide-react';

interface EmailSequenceFormProps {
  onSubmit: (data: EmailSequenceData) => void;
  isGenerating: boolean;
}

const EmailSequenceForm = ({ onSubmit, isGenerating }: EmailSequenceFormProps) => {
  const [formData, setFormData] = useState<EmailSequenceData>({
    prospectName: '',
    companyName: '',
    websiteUrl: '',
    linkedinUrl: '',
    industry: '',
    companySize: '',
    leadSource: '',
    painPoint: '',
    emailTone: 'professional',
    numEmails: 3,
    timeGapDays: 3,
    productFocus: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field: keyof EmailSequenceData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Information */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold">Basic Information</h3>
            
            <div className="space-y-2">
              <Label htmlFor="prospectName">Prospect Name *</Label>
              <Input
                id="prospectName"
                value={formData.prospectName}
                onChange={(e) => handleInputChange('prospectName', e.target.value)}
                placeholder="John Smith"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Company Name *</Label>
              <Input
                id="companyName"
                value={formData.companyName}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                placeholder="Acme Corp"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website URL</Label>
              <Input
                id="websiteUrl"
                value={formData.websiteUrl}
                onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                placeholder="https://example.com"
                type="url"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="linkedinUrl">LinkedIn URL</Label>
              <Input
                id="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                placeholder="https://linkedin.com/in/johnsmith"
                type="url"
              />
            </div>
          </CardContent>
        </Card>

        {/* Company Details */}
        <Card>
          <CardContent className="p-4 space-y-4">
            <h3 className="text-lg font-semibold">Company Details</h3>
            
            <div className="space-y-2">
              <Label htmlFor="industry">Industry</Label>
              <Input
                id="industry"
                value={formData.industry}
                onChange={(e) => handleInputChange('industry', e.target.value)}
                placeholder="Technology, Healthcare, Finance..."
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="companySize">Company Size</Label>
              <Select 
                value={formData.companySize} 
                onValueChange={(value) => handleInputChange('companySize', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="startup">Startup (1-10)</SelectItem>
                  <SelectItem value="small">Small (11-50)</SelectItem>
                  <SelectItem value="medium">Medium (51-200)</SelectItem>
                  <SelectItem value="large">Large (201-1000)</SelectItem>
                  <SelectItem value="enterprise">Enterprise (1000+)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="leadSource">Lead Source</Label>
              <Select 
                value={formData.leadSource} 
                onValueChange={(value) => handleInputChange('leadSource', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="How did you find this lead?" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="referral">Referral</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="cold-call">Cold Call</SelectItem>
                  <SelectItem value="website">Website</SelectItem>
                  <SelectItem value="event">Event/Conference</SelectItem>
                  <SelectItem value="social-media">Social Media</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="painPoint">Pain Point/Challenge</Label>
              <Textarea
                id="painPoint"
                value={formData.painPoint}
                onChange={(e) => handleInputChange('painPoint', e.target.value)}
                placeholder="What specific challenge does this prospect face?"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Email Configuration */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <h3 className="text-lg font-semibold">Email Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="numEmails">Number of Emails</Label>
              <Select 
                value={formData.numEmails.toString()} 
                onValueChange={(value) => handleInputChange('numEmails', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map(num => (
                    <SelectItem key={num} value={num.toString()}>{num} emails</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="timeGapDays">Days Between Emails</Label>
              <Select 
                value={formData.timeGapDays.toString()} 
                onValueChange={(value) => handleInputChange('timeGapDays', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 day</SelectItem>
                  <SelectItem value="2">2 days</SelectItem>
                  <SelectItem value="3">3 days</SelectItem>
                  <SelectItem value="5">5 days</SelectItem>
                  <SelectItem value="7">1 week</SelectItem>
                  <SelectItem value="14">2 weeks</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="emailTone">Email Tone</Label>
              <Select 
                value={formData.emailTone} 
                onValueChange={(value) => handleInputChange('emailTone', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="friendly">Friendly</SelectItem>
                  <SelectItem value="direct">Direct</SelectItem>
                  <SelectItem value="consultative">Consultative</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="productFocus">Product/Service Focus</Label>
            <Textarea
              id="productFocus"
              value={formData.productFocus}
              onChange={(e) => handleInputChange('productFocus', e.target.value)}
              placeholder="What specific product or service are you selling to this prospect?"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      <Button 
        type="submit" 
        className="w-full" 
        disabled={isGenerating || !formData.prospectName || !formData.companyName}
      >
        {isGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Generating Email Sequence...
          </>
        ) : (
          'Generate Email Sequence'
        )}
      </Button>
    </form>
  );
};

export default EmailSequenceForm;